import React, { useState } from 'react';
import { Form, Input, Button, message } from 'antd';
import { MailOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import AppLinksFooter from '../AppLinksFooter';

const AdminLoginScreen = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { adminLogin } = useAdminAuth();

  const onFinish = async (values) => {
    setLoading(true);
    
    const result = await adminLogin(values.email, values.password);
    
    if (result.success) {
      message.success(result.message);
      navigate('/admin/dashboard');
    } else {
      message.error(result.message);
      form.setFields([
        {
          name: 'password',
          errors: [result.message],
        },
      ]);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-400 via-cyan-500 to-cyan-600 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img 
              src={`${process.env.PUBLIC_URL}/images/logo.png`}
              alt="GUFFPUFF" 
              className="h-20 w-auto"
            />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-cyan-100">Secure Access Control Panel</p>
        </div>

        {/* Login Card */}
        <div className="bg-white backdrop-blur-xl rounded-3xl shadow-2xl p-8 w-full border border-gray-200">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Administrator Login</h2>
            <p className="text-gray-500 text-sm">Enter your credentials to access the admin panel</p>
          </div>

          <Form
            form={form}
            name="adminLogin"
            onFinish={onFinish}
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please input your email!' },
                { type: 'email', message: 'Please enter a valid email!' }
              ]}
            >
              <Input
                prefix={<MailOutlined className="text-cyan-500" />}
                placeholder="Admin Email"
                size="large"
                className="rounded-xl py-3"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: 'Please input your password!' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="text-cyan-500" />}
                placeholder="Password"
                size="large"
                className="rounded-xl py-3"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            <Form.Item className="mb-6">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                className="w-full bg-gradient-to-r from-cyan-400 to-cyan-600 hover:from-cyan-500 hover:to-cyan-700 border-0 rounded-xl h-12 font-bold text-base shadow-lg"
              >
                {loading ? 'Authenticating...' : 'Login to Admin Panel'}
              </Button>
            </Form.Item>
          </Form>
        </div>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <p className="text-white text-sm">
            🔒 All admin activities are logged and monitored
          </p>
          <div className="mt-3 flex items-center justify-center gap-4">
            <a
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-100 text-xs hover:text-white underline underline-offset-2 transition-colors"
            >
              Terms &amp; Conditions
            </a>
            <span className="text-cyan-300 text-xs">•</span>
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-100 text-xs hover:text-white underline underline-offset-2 transition-colors"
            >
              Privacy Policy
            </a>
          </div>
          <AppLinksFooter variant="light" className="mt-3" />
        </div>
      </div>
    </div>
  );
};

export default AdminLoginScreen;
